import { Box, Button } from '@stitch-fix/mode-react';
import classNames from 'classnames';
import React from 'react';
import styles from './style.module.scss';
import { JsonRteRenderer } from '../JsonRteRenderer';
import { isEmpty } from '../JsonRteRenderer/JsonRteRenderer';
import { HeroProps } from './Hero';
import { GetStartedButton } from '../GetStartedButton';
import { buttonColorToButtonVariant } from '../ModuleFooter/ModuleFooter';
import { useFrontDoorOffer } from '../../hooks/useFrontDoorOffer';
type ContentItem = Omit<Required<NonNullable<HeroProps['items']>[number]>, 'media'>;
export type ContentItemsProps = {
  items: ContentItem[];
  activeSlideIndex: number;
  enableOfferOverride?: boolean;
};
export const ContentItems = ({
  items,
  activeSlideIndex,
  enableOfferOverride = false
}: ContentItemsProps) => {
  const activeItem = items[activeSlideIndex];
  const {
    contentBackground
  } = activeItem ?? {};
  const {
    offer
  } = useFrontDoorOffer(!enableOfferOverride);
  const hasAnyCta = items.some(item => enableOfferOverride && offer || item.CtaButton || item.cta.title && item.cta.href);
  return <Box className={classNames(styles.contentWrapper, {
    [styles.whiteContentBackground]: contentBackground === 'white',
    [styles.withoutAnyCta]: !hasAnyCta
  })}>
      <Box data-testid="content" className={classNames(styles.content, {
      [styles.withoutAnyCta]: !hasAnyCta
    })}>
        {items.map((item, index) => {
        // Render all the items, but only the active one will be visible
        // this is to ensure that the content is as wide and tall as largest
        // item to prevent content from jumping around when switching slides

        return <div
        // eslint-disable-next-line react/no-array-index-key
        key={index} aria-hidden={index !== activeSlideIndex} className={classNames(styles.contentItem, {
          [styles.active]: index === activeSlideIndex
        })}>
              <ContentItem {...item} enableOfferOverride={enableOfferOverride} offer={offer} />
            </div>;
      })}
      </Box>
    </Box>;
};
const ContentItem = ({
  fontColor,
  title,
  subtitle,
  cta,
  buttonColor,
  CtaButton,
  enableOfferOverride = false,
  offer,
  ctaSubtext
}: ContentItem & {
  enableOfferOverride?: boolean;
  offer: ReturnType<typeof useFrontDoorOffer>['offer'];
}) => {
  const renderCta = () => {
    if (enableOfferOverride && offer) {
      const ctaText = (() => {
        switch (offer.cell) {
          case 'percentage':
            return `Take the quiz, get ${offer.discount.percentage}% off`;
          case 'no-promo':
            return 'Take the quiz';
          default:
            return `Take the quiz, get $${offer.discount.amountCents / 100} off`;
        }
      })();
      return <Button as="a" variant={buttonColorToButtonVariant(buttonColor)} href="/signup">
          {ctaText}
        </Button>;
    }
    if (CtaButton) {
      return CtaButton;
    }
    if (cta.title && cta.href) {
      return <GetStartedButton copy={cta.title} href={cta.href} variant={buttonColorToButtonVariant(buttonColor)} eventLocation="hero" />;
    }
    return null;
  };
  const ctaComponent = renderCta();
  return <>
      <Box className={classNames({
      [styles.textShadow]: fontColor === 'white'
    })} mb={ctaComponent ? 1.5 : 0}>
        <JsonRteRenderer data={title} options={{
        textProps: {
          h1: {
            color: fontColor
          }
        }
      }} enableOfferOverride={enableOfferOverride} />
        {!isEmpty(subtitle?.children) && <JsonRteRenderer data={subtitle} options={{
        textProps: {
          p: {
            color: fontColor,
            spacingBottom: false
          },
          li: {
            color: fontColor,
            spacingBottom: false
          }
        }
      }} enableOfferOverride={enableOfferOverride} />}
      </Box>
      {ctaComponent}
      {!isEmpty(ctaSubtext?.children) && <Box mt={1.5}>
          <JsonRteRenderer data={ctaSubtext} options={{
        textProps: {
          p: {
            color: fontColor,
            spacingBottom: false,
            setting: 'body-small'
          }
        }
      }} />
        </Box>}
    </>;
};